import _ from 'lodash'
import { useEffect, useRef } from 'react'
import { CasinoGameCard } from '@/components/casino/lobby/game-card/CasinoGameCard'
import { CasinoGameCardList } from '@/components/casino/lobby/game-card/CasinoGameCardList'
import { CasinoGameCardSkeleton } from '@/components/casino/lobby/game-card/CasinoGameCardSkeleton'
import { CASINO_GAMES_LOAD_MORE } from '@/config/casino'
import { useInfiniteCasinoGames } from '@/hooks/use-infinite-casino-games'
import { BlackjackIcon } from '@/components/casino/icons'

interface CasinoTypeGamesContentProps {
	typeId: string
	typeName?: string
}

export function CasinoTypeGamesContent({ typeId, typeName }: CasinoTypeGamesContentProps) {
	const { games, isLoading, isLoadingMore, isError, hasMore, loadMore } = useInfiniteCasinoGames({ type_id: typeId })
	const loadMoreRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const node = loadMoreRef.current

		if (!node || !hasMore || isLoadingMore) {
			return
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					loadMore()
				}
			},
			{ rootMargin: '240px' },
		)

		observer.observe(node)

		return () => observer.disconnect()
	}, [games.length, hasMore, isLoadingMore, loadMore])

	return (
		<section className='flex flex-col gap-4'>
			<div className='flex items-center gap-3'>
				<BlackjackIcon className='h-7 w-7 text-subtle-foreground' />
				<h2 className='text-2xl font-semibold tracking-[-0.12px] text-foreground'>{typeName ?? 'Casino Games'}</h2>
			</div>

			{isLoading || isError || _.isEmpty(games) ? (
				<CasinoGameCardList
					games={games}
					isLoading={isLoading}
					isError={isError}
					layout='grid'
					skeletonCount={CASINO_GAMES_LOAD_MORE}
					emptyMessage='No games are available in this type right now.'
				/>
			) : (
				<>
					<div className='grid grid-cols-[repeat(auto-fill,150px)] justify-center gap-6'>
						{_.map(games, (game) => (
							<CasinoGameCard key={game.id} game={game} />
						))}
						{isLoadingMore ? _.times(CASINO_GAMES_LOAD_MORE, (index) => <CasinoGameCardSkeleton key={`type-games-loading-${index}`} />) : null}
					</div>
					{hasMore ? <div ref={loadMoreRef} className='h-px' aria-hidden /> : null}
				</>
			)}
		</section>
	)
}
